import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { MapPin, Truck, Users, TreePine } from 'lucide-react';

const Login = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1636294734011-3a642de28d8a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Camping in India"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <h1 className="text-4xl font-bold text-white mb-4 tracking-tight">
            Pollen Dots Digital Passport
          </h1>
          <p className="text-lg text-white/80">
            Travel. Connect. Care. — Care-based travel experiences rooted in people, place and purpose.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#FDFDFC]">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 sm:mb-12">
            <img src="/pollen-logo.png" alt="Pollen Dots Digital Passport" className="h-14 sm:h-16 w-auto mx-auto mb-4 sm:mb-6" />
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-2 sm:mb-3">
              Welcome Back
            </h2>
            <p className="text-muted-foreground">
              Sign in to continue your journey
            </p>
          </div>

          <Button
            onClick={handleGoogleLogin}
            size="lg"
            className="w-full h-12 text-base font-medium bg-[#1A1A19] hover:bg-[#2a2a29] text-white"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>

          <div className="mt-12 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-[#F5F5F0]">
              <MapPin className="w-5 h-5 text-[#D94E28]" />
              <span className="text-sm font-medium text-foreground">Map Locations</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-[#F5F5F0]">
              <Users className="w-5 h-5 text-[#4A6FA5]" />
              <span className="text-sm font-medium text-foreground">Team Research</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-[#F5F5F0]">
              <Truck className="w-5 h-5 text-[#2E7D32]" />
              <span className="text-sm font-medium text-foreground">Track Operators</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-[#F5F5F0]">
              <TreePine className="w-5 h-5 text-[#8E44AD]" />
              <span className="text-sm font-medium text-foreground">Tourism Spots</span>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            Internal platform for The Caravan Network team
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
